import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { Role } from "./types";

export interface IngestionDashboard {
  documents: { status: string; count: number }[];
  queues: { name: string; state: string; count: number }[];
  stages24h: { stage: string; count: number; avgMs: number | null; lastAt: string | null }[];
  ready: { lastHour: number; lastDay: number };
  recentErrors: {
    id: number;
    documentId: number;
    originalName: string;
    stage: string;
    message: string | null;
    createdAt: string;
  }[];
  inbox: { status: string; count: number }[];
  embedding: { model: string; vectors: number };
  inboxDir: string;
  inboxMode: string;
}

export interface FailedDocument {
  id: number;
  originalName: string;
  title: string;
  companyId: number;
  docTypeId: number | null;
  status: string;
  error: string | null;
  sourceKind: string;
  sourceRef: string | null;
  updatedAt: string;
}

export interface InboxFile {
  id: number;
  relPath: string;
  sizeBytes: number | null;
  mtime: string | null;
  status: string;
  documentId: number | null;
  error: string | null;
  firstSeenAt: string;
  processedAt: string | null;
  /** null: the classic inbox folder. */
  sourceId: number | null;
  sourceName: string | null;
}

/** A folder source (GET /api/admin/sources). */
export interface Source {
  id: number;
  name: string;
  path: string;
  mode: "reference" | "copy";
  layout: "any" | "company-type";
  companyId: number | null;
  docTypeId: number | null;
  scan: "interval" | "live";
  scanMinutes: number;
  windowsPrefix: string | null;
  enabled: boolean;
  state: "idle" | "scanning" | "ok" | "unavailable" | "guarded" | "error";
  lastScanAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  files: number;
  documents: number;
  missing: number;
  missingPending: number;
  problems: number;
  excluded: number;
  indexing: number;
}

export interface SourceFile {
  id: number;
  relPath: string;
  sizeBytes: number | null;
  status: string;
  documentId: number | null;
  error: string | null;
  present: boolean;
  missingScans: number;
  missingSince: string | null;
  excluded: boolean;
}

export interface AdminUser {
  id: number;
  username: string;
  displayName: string;
  role: Role;
  locale: string;
  companyIds: number[] | null;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AdminCompany {
  id: number;
  code: string | null;
  nameHy: string;
  nameRu: string | null;
  nameEn: string | null;
  taxId: string | null;
  folderName: string | null;
  mulberryBaseUrl: string | null;
  cloudLlmAllowed: boolean;
  active: boolean;
  aliases: string[];
  documents: number;
}

export interface AdminDocType {
  id: number;
  code: string | null;
  nameHy: string;
  nameRu: string | null;
  nameEn: string | null;
  folderName: string | null;
  sortOrder: number;
  keywords: string[];
  documents: number;
}

export interface Synonym {
  id: number;
  lang: string;
  term: string;
  variants: string[];
  enabled: boolean;
}

export interface MetadataImport {
  id: number;
  filename: string;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  rows_total: number;
  rows_matched: number;
  rows_unmatched: number;
  created_at: string;
}

export interface SearchLogEntry {
  id: number;
  query: string;
  queryNorm: string;
  mode: string;
  resultDocs: number;
  tookMs: number;
  createdAt: string;
  username: string | null;
  clicks: number;
}

export interface Judgment {
  id: number;
  query: string;
  documentId: number;
  grade: number;
  userId: number | null;
  username: string | null;
  createdAt: string;
  title: string | null;
  regNumber: string | null;
}

export function useAdminQuery<T>(key: string, url: string, refetchMs?: number) {
  return useQuery({
    queryKey: ["admin", key],
    queryFn: ({ signal }) => api<T>(url, { signal }),
    ...(refetchMs ? { refetchInterval: refetchMs } : {}),
  });
}

/** Mutation helper that refreshes the given admin lists on success. */
export function useAdminMutation<TVars, TResult = unknown>(
  run: (vars: TVars) => Promise<TResult>,
  invalidate: string[],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      for (const key of invalidate)
        void queryClient.invalidateQueries({ queryKey: ["admin", key] });
      void queryClient.invalidateQueries({ queryKey: ["lookups"] });
    },
  });
}

/** Evaluation run started from the admin UI (GET /api/admin/eval/runs/:id). */
export interface EvalConfigResult {
  name: string;
  recall5: number;
  recall10: number;
  recall50: number;
  mrr10: number;
  ndcg10: number;
  medianMs: number;
  p95Ms: number;
  queries: {
    id: string;
    query: string;
    relevantCount: number;
    unresolved: string[];
    firstRank: number | null;
    recall10: number;
    missed: number[];
  }[];
}

export interface EvalRunSummary {
  id: string;
  status: "running" | "done" | "failed";
  startedAt: string;
  finishedAt: string | null;
  configs: string[];
  queries: number;
  reportFile: string | null;
  error: string | null;
  summary: Omit<EvalConfigResult, "queries">[] | null;
}

export interface EvalRun extends Omit<EvalRunSummary, "summary"> {
  log: string[];
  result: { results: EvalConfigResult[]; skipped: string[] } | null;
}
